import React, { useState, useEffect, useCallback, useRef } from 'react';
import { __, sprintf } from '@wordpress/i18n';
import { isEmpty, isNil, last, uniq, is, pathOr } from 'ramda';
import { wrap } from 'object-path-immutable';
import { useNavigate } from 'react-router-dom';
import { klona } from 'klona';

// tools
import getBandoSeverity from '../../../../helpers/getBandoSeverity';
import getBandoLabel from '../../../../helpers/getBandoLabel';
import set404FromErrorResponse from '../../../../helpers/set404FromErrorResponse';
import renderHtmlContent from '../../../../helpers/renderHtmlContent';

// api
import CompanyDocumentsService from '../../../../service/company-documents-service';
import CompanyService from '../../../../service/company-service';
import ApplicationService from '../../../../service/application-service';

// store
import { storeGet, storeSet } from '../../../../store';

// components
import { FilterMatchMode, FilterOperator } from 'primereact/api';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Tag } from 'primereact/tag';
import ProperBandoLabel from '../../../../components/ProperBandoLabel';
import { classNames } from 'primereact/utils';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Editor } from 'primereact/editor';
import { Toast } from 'primereact/toast';


const DEFAULT_AMENDMENT_DESCRIPTION = '<p>Spett.le Fornitore, </p><p><br></p><p>la informiamo che uno o più documenti necessitano di aggiornamento. Si prega di rivedere la propria posizione per concludere l\'iter di qualificazione. </p><p><br></p><p>Cordiali saluti</p>';

// Deve restare allineata a CompanyIntegrationRequestDao.APERTURA_PREDEFINITA nel backend:
// se l'operatore non tocca nulla, il backend riscriverebbe comunque questa stessa frase.
const APERTURA_PREDEFINITA = '<p>a seguito della verifica della documentazione caricata nel Portale Fornitori, per proseguire con la qualificazione occorre integrare quanto segue:</p>';

const DocumentsToEvaluateTable = ({ companyId, readOnly = false, companyStatus, applicationId = null }) => {
    const navigate = useNavigate();
    const [currentStatus, setCurrentStatus] = useState(companyStatus);
    const [rawDocsData, setRawDocsData] = useState({});
    const [docs, setDocs] = useState([]);
    const [altroDocs, setAltroDocs] = useState([]);
    const [filters, setFilters] = useState(null);
    const [loading, setLoading] = useState(false);
    const [statuses, setStatuses] = useState([]);
    const [isVisibleAmendmentDialog, setIsVisibleAmendmentDialog] = useState(false);
    const [isVisibleMotivationDialog, setIsVisibleMotivationDialog] = useState(false);
    const [motivationContent, setMotivationContent] = useState('');
    const [amendmentData, setAmendmentData] = useState({
        title: '',
        description: ''
    });
    const toast = useRef(null);

    useEffect(() => {
        if (!isEmpty(rawDocsData) && !isNil(rawDocsData.documentCategories)) {
            const copyRawDocs = klona(rawDocsData);

            const esg1Categories = [];
            const esg2Categories = [];
            const esg3Categories = [];
            copyRawDocs?.esgCategories?.forEach(cat => {
                if (cat.type === 'ESG1') esg1Categories.push(cat);
                else if (cat.type === 'ESG2') esg2Categories.push(cat);
                // Autovalutazione ESG: il modulo firmato depositato dal questionario
                else if (cat.type === 'ESG3') esg3Categories.push(cat);
            });

            const formattedCategories = getFormattedData(copyRawDocs.documentCategories, [copyRawDocs.lookUpDataValue]);
            const formattedESG1 = getFormattedData(esg1Categories, [copyRawDocs.lookUpDataValue]);
            const formattedESG2 = getFormattedData(esg2Categories, [copyRawDocs.lookUpDataValue]);
            const formattedESG3 = getFormattedData(esg3Categories, [copyRawDocs.lookUpDataValue]);

            const esgRows = [
                ...formattedESG1.filter(d => d.fileId).map(d => ({ ...d, esgType: 'ESG1' })),
                ...formattedESG2.filter(d => d.fileId).map(d => ({ ...d, esgType: 'ESG2' })),
                ...formattedESG3.filter(d => d.fileId).map(d => ({ ...d, esgType: 'ESG3' })),
            ];

            const formattedISO = getFormattedData(copyRawDocs.isocategories || [], [copyRawDocs.lookUpDataValue]);
            const isoRows = formattedISO.filter(d => d.fileId).map(d => ({ ...d, isoType: 'ISO' }));

            const altroData = formattedCategories.filter(d => d.categoryName === 'ALTRO');
            const nonAltroCategories = formattedCategories.filter(d => d.categoryName !== 'ALTRO');
            const allDocs = [...nonAltroCategories, ...esgRows, ...isoRows];
            const sorted = [...allDocs].sort((a, b) => 0);
            setDocs(sorted);
            setAltroDocs(altroData);
            setStatuses(uniq(allDocs.map(o => o.status)));
            initFilters();
        }
    }, [rawDocsData]);

    useEffect(() => {
        getDocsRequest(companyId);
    }, [companyId]);

    // Bug 4 (Valentina 7/9): il questionario ESG segnato deve poter far scattare la
    // richiesta di integrazioni anche quando i documenti sono tutti a posto. Il backend
    // (createForCompany) le include gia'; qui contiamo le risposte segnate da integrare
    // per accendere il pulsante. daIntegrare = true e' lo stesso discriminante del backend.
    const [risposteDaIntegrare, setRisposteDaIntegrare] = useState(0);
    useEffect(() => {
        if (!applicationId) { setRisposteDaIntegrare(0); return; }
        ApplicationService.getAnswersReview(applicationId, (resp) => {
            const voci = (resp.status === 'SUCCESS' && Array.isArray(resp.data)) ? resp.data : [];
            setRisposteDaIntegrare(voci.filter(r => r.daIntegrare === true || r.stato === 'DA_INTEGRARE').length);
        }, () => setRisposteDaIntegrare(0));
    }, [applicationId]);

    // Una sola richiesta aperta per volta: se il fornitore deve ancora rispondere, il pulsante
    // aggiorna quella richiesta (le voci nuove si aggiungono) invece di crearne una seconda.
    const [richiestaAperta, setRichiestaAperta] = useState(false);
    const [ricaricaStorico, setRicaricaStorico] = useState(0);
    useEffect(() => {
        if (!applicationId) { setRichiestaAperta(false); return; }
        ApplicationService.getIntegrationHistory(applicationId, (resp) => {
            const list = (resp.status === 'SUCCESS' && Array.isArray(resp.data)) ? resp.data : [];
            setRichiestaAperta(list.some(r => r.stato && r.stato !== 'CHIUSA'));
        }, () => setRichiestaAperta(false));
    }, [applicationId, ricaricaStorico]);

    const getDocsRequest = (companyId) => {
        setLoading(true);
        CompanyDocumentsService.getCompanyDocumentsByCategory(getCallback, errGetCallbacks, [
            ['companyId', companyId]
        ]);
    }

    const getCallback = (resp) => {
        if (resp.status === 'SUCCESS' && resp.data) {
            setRawDocsData(resp.data);
        }
        setLoading(false);
    }

    const errGetCallbacks = () => {
        setLoading(false);
    }

    const getFormattedData = (data, extraKeys) => {
        return data.map((d, index) => {
            d.companyCategories = extraKeys;
            d.uniqueRowId = `${d.id}-${index}`;
            if (d.type !== 'ALTRO') {
                const lastFile = last(d.files);
                if (lastFile) {
                    d.json = lastFile.json;
                    d.fileId = lastFile.id;
                    d.statusDoc = lastFile.status;
                    // Tre stati, non due: senza motivo il documento non e' ancora stato guardato.
                    // Se e' arrivata una versione nuova non ancora giudicata, la casella
                    // torna scoperta: si chiama "Aggiornato", non "In attesa".
                    d.statusEvaluation = lastFile.isValid
                        ? 'APPROVED'
                        : (lastFile.validationNote
                            ? 'REJECTED'
                            : (lastFile.previousDocumentId ? 'DOCUMENT_UPDATED' : 'AWAITING'));
                    d.filePath = lastFile.filePath;
                    d.isValid = lastFile.isValid;
                    d.validationNote = lastFile.validationNote;
                    d.previousDocumentId = lastFile.previousDocumentId;
                    d.createdDate = is(String, lastFile.createdDate) ? new Date(lastFile.createdDate) : (lastFile.createdDate ? lastFile.createdDate : '');
                    d.updatedDate = is(String, lastFile.updatedDate) ? new Date(lastFile.updatedDate) : (lastFile.updatedDate ? lastFile.updatedDate : '');
                    d.expirationDate = is(String, lastFile.expirationDate) ? new Date(lastFile.expirationDate) : (lastFile.expirationDate ? lastFile.expirationDate : '');
                } else {
                    d.fileId = 0;
                    d.statusDoc = 'NOT_AVAILABLE';
                    d.statusEvaluation = 'AWAITING';
                    d.filePath = '';
                    d.isValid = false;
                }
            }
            return d;
        });
    };

    const initFilters = () => {
        setFilters({
            categoryName: {
                operator: FilterOperator.AND,
                constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }]
            },
            createdDate: {
                operator: FilterOperator.AND,
                constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }]
            }
        });
    };

    const descriptionBodyTemplate = (rowData) => {
        return (
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <span>{rowData.description}</span>
                {rowData.esgType && <Tag value={rowData.esgType} severity="info"/>}
                {rowData.isoType && <Tag value={rowData.isoType} severity="warning"/>}
            </div>
        );
    };

    const statusDocBodyTemplate = (rowData) => {
        return <ProperBandoLabel status={rowData.statusDoc}/>;
    };

    const statusEvaluationBodyTemplate = (rowData) => {
        return <ProperBandoLabel status={rowData.statusEvaluation}/>;
    };

    const statusFilterTemplate = (options) => {
        return <Dropdown value={options.value} options={statuses}
                         valueTemplate={getBandoLabel(options.value)}
                         onChange={(e) => options.filterCallback(e.value, options.index)}
                         itemTemplate={statusItemTemplate} placeholder={__('Scegli uno', 'htr')}
                         className="p-column-filter"/>;
    };

    const statusItemTemplate = (option) => {
        return <Tag value={getBandoLabel(option)} severity={getBandoSeverity(option)}/>;
    };

    const formatDate = (value) => {
        return value.toLocaleDateString ? value.toLocaleDateString('it-IT', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        }) : '';
    };

    const dateCreatedBodyTemplate = (rowData) => {
        return formatDate(rowData.createdDate);
    };

    const dateFilterTemplate = (options) => {
        return <Calendar value={options.value} onChange={(e) => options.filterCallback(e.value, options.index)}
                         dateFormat="dd/mm/yy" placeholder="dd/mm/yyyy" mask="99/99/9999"/>;
    };

    const hideMotivationDialog = () => {
        setIsVisibleMotivationDialog(false);
        setMotivationContent('');
    };

    const headerMotivationDialog = () => (
        <span>{__('Motivazione', 'htr')}</span>
    );

    const hideAmendmentDialog = () => {
        setIsVisibleAmendmentDialog(false);
        const newData = wrap({})
            .set(['title'], '')
            .set(['description'], '')
            .value();
        setAmendmentData(newData);
    }

    const headerAmendmentDialog = () => {
        return <span>{__('Messaggio Sollecito', 'htr')}</span>
    }

    const footerAmendmentDialog = () => {
        return <div>
            <Button type="button" label={__('Annulla', 'htr')} onClick={hideAmendmentDialog} outlined/>
            <Button
                type="button"
                disabled={loading || !isValidForm()}
                label={__('Salva', 'htr')} onClick={doSendAmendment}/>
        </div>
    }

    const renderEditorHeader = () => {
        return (
            <span className="ql-formats">
                <button className="ql-bold" aria-label="Bold"></button>
                <button className="ql-italic" aria-label="Italic"></button>
                <button className="ql-underline" aria-label="Underline"></button>
                <button className="ql-link" aria-label="Link"></button>
                <button className="ql-list" value="ordered"></button>
                <button className="ql-header" value="2"></button>
                <button className="ql-header" value="3"></button>
                <button className="ql-blockquote"></button>
                <button className="ql-list" value="bullet"></button>
                <button className="ql-indent" value="-1"></button>
                <button className="ql-indent" value="+1"></button>
            </span>
        );
    };

    const editorHeader = renderEditorHeader();

    const doSendAmendment = useCallback(() => {
        setLoading(true);
        CompanyService.sendCompanyAmendmentEmail(companyId, amendmentData, sendEmail, errSendEmail)
    }, [amendmentData]);

    const sendEmail = (resp) => {
        if (resp.status === 'SUCCESS') {
            if (toast.current) {
                toast.current.show({
                    severity: 'success',
                    summary: '',
                    detail: resp.message
                });
            }
        }
        setLoading(false);
        hideAmendmentDialog();
    }

    const errSendEmail = (resp) => {
        if (toast.current && resp.message) {
            toast.current.show({
                severity: 'error',
                summary: '',
                detail: resp.message
            });
        }
        set404FromErrorResponse(resp);
        setLoading(false);
        hideAmendmentDialog();
    }

    const isInvalidField = (data, key) => isEmpty(data[key]) || isNil(data[key]);

    const onUpdateFieldValue = useCallback((value, name) => {
        const newData = wrap(amendmentData).set([name], value).value();
        setAmendmentData(newData);
    }, [amendmentData]);

    const isValidForm = useCallback(() => {
        return !isEmpty(amendmentData.title) && !isEmpty(amendmentData.description)
    }, [amendmentData]);

    const actionsBodyTemplate = (rowData) => {
        const motivation = pathOr(null, ['json', 'motivation'], rowData);
        return <div className="appPageSection__iconActions">
            {(rowData.filePath || motivation)
                ? <Button icon="pi pi-eye" rounded
                          onClick={() => {
                              if (rowData.filePath) {
                                  apriDocumento(rowData.fileId, rowData.filePath, rowData.description);
                              } else {
                                  setMotivationContent(motivation);
                                  setIsVisibleMotivationDialog(true);
                              }
                          }}
                          outlined
                          severity="info"
                          aria-label={__('Mostra', 'htr')}/>
                : null}
            {rowData.statusDoc === 'NOT_AVAILABLE' || isNil(rowData.filePath)
                ? null
                : <Button icon="pi pi-download" rounded
                          disabled={rowData.statusDoc === 'NOT_AVAILABLE'}
                          onClick={() => scaricaDocumento(rowData.fileId)}
                          severity="info"
                          aria-label={__('Scarica', 'htr')}/>}
            {isNil(rowData.filePath)
                ? null
                : <Button icon="pi pi-calendar" rounded outlined
                          severity="secondary"
                          onClick={() => apriTimeline(rowData.fileId, rowData.description)}
                          aria-label={__('Storia dei caricamenti', 'htr')}/>}
            {/* Il modulo del Questionario ESG (ESG3) non ha i pollici: si valuta dalle risposte, sotto. */}
            {rowData.statusDoc === 'NOT_AVAILABLE' || rowData.esgType === 'ESG3'
                ? null
                : <Button icon="pi pi-thumbs-up" rounded outlined
                          disabled={readOnly || rowData.statusDoc === 'NOT_AVAILABLE'}
                          onClick={() => handleValidate(rowData.fileId, true)}
                          severity={!isNil(rowData.isValid) && rowData.isValid ? 'success' : 'secondary'}
                          aria-label={__('Si', 'htr')}/>}
            {rowData.statusDoc === 'NOT_AVAILABLE' || rowData.esgType === 'ESG3'
                ? null
                : <Button icon="pi pi-thumbs-down" rounded outlined
                          disabled={readOnly || rowData.statusDoc === 'NOT_AVAILABLE'}
                          onClick={() => chiediMotivo(rowData.fileId, rowData.validationNote)}
                          severity={!isNil(rowData.isValid) && !rowData.isValid ? 'danger' : 'secondary'}
                          aria-label={__('No', 'htr')}/>}
            {rowData.esgType === 'ESG3'
                ? <Tag value={__('Si valuta dalle risposte', 'htr')} severity="info"/>
                : null}
            {!isNil(rowData.validationNote) && rowData.validationNote !== ''
                ? <Button icon="pi pi-comment" rounded outlined
                          severity="danger"
                          title={rowData.validationNote}
                          onClick={() => chiediMotivo(rowData.fileId, rowData.validationNote)}
                          aria-label={__('Motivo del rifiuto', 'htr')}/>
                : null}
        </div>
    }

    const [motivoVisibile, setMotivoVisibile] = useState(false);
    const [motivoTesto, setMotivoTesto] = useState('');
    const [motivoDocId, setMotivoDocId] = useState(null);
    const [integrazioniVisibile, setIntegrazioniVisibile] = useState(false);
    const [integrazioniGiorni, setIntegrazioniGiorni] = useState(10);
    const [timelineVisibile, setTimelineVisibile] = useState(false);
    const [timelineVoci, setTimelineVoci] = useState([]);
    const [timelineTitolo, setTimelineTitolo] = useState('');

    const apriTimeline = (idFile, titolo) => {
        setTimelineTitolo(titolo || '');
        CompanyDocumentsService.getDocumentTimeline(idFile, (resp) => {
            if (resp.status === 'SUCCESS') {
                setTimelineVoci(resp.data || []);
                setTimelineVisibile(true);
            }
        }, () => {});
    }

    const statoVersione = (v) => {
        if (v.isValid) return 'APPROVED';
        if (v.validationNote) return 'REJECTED';
        return 'AWAITING';
    }

    const [docVisibile, setDocVisibile] = useState(false);
    const [docUrl, setDocUrl] = useState('');
    const [docNome, setDocNome] = useState('');
    const [docTipo, setDocTipo] = useState('');

    const tipoDaPercorso = (percorso) => {
        const p = (percorso || '').toLowerCase().split('?')[0];
        if (p.endsWith('.pdf')) return 'pdf';
        if (p.endsWith('.png') || p.endsWith('.jpg') || p.endsWith('.jpeg')
            || p.endsWith('.gif') || p.endsWith('.webp')) return 'immagine';
        return 'altro';
    }

    // L'occhio apre, la freccia scarica: due pulsanti, due comportamenti.
    const apriDocumento = (idFile, percorso, titolo) => {
        const tipo = tipoDaPercorso(percorso);
        if (tipo === 'altro') { scaricaDocumento(idFile); return; }
        CompanyDocumentsService.getDocumentLink(idFile, (resp) => {
            if (resp.status === 'SUCCESS') {
                setDocUrl(resp.data);
                setDocTipo(tipo);
                setDocNome(titolo || '');
                setDocVisibile(true);
            }
        }, () => {}, [['inline', true]]);
    }

    const scaricaDocumento = (idFile) => {
        CompanyDocumentsService.getDocumentLink(idFile, (resp) => {
            if (resp.status === 'SUCCESS') window.open(resp.data, '_blank');
        }, () => {}, [['inline', false]]);
    }

    const [anteprimaMail, setAnteprimaMail] = useState('');
    const [anteprimaInCorso, setAnteprimaInCorso] = useState(false);
    // Unica parte discorsiva modificabile. Il resto della mail e' generato e non si tocca.
    const [apertura, setApertura] = useState(APERTURA_PREDEFINITA);
    const timerAnteprima = useRef(null);

    const caricaAnteprima = (giorni, testoApertura) => {
        setAnteprimaInCorso(true);
        CompanyDocumentsService.previewIntegrationRequest(
            companyId,
            { responseDays: Number(giorni) || 10, note: testoApertura },
            (resp) => {
                setAnteprimaInCorso(false);
                if (resp.status === 'SUCCESS') setAnteprimaMail(resp.data);
            },
            () => setAnteprimaInCorso(false)
        );
    }

    // L'anteprima si rinfresca quando l'operatore smette di scrivere, non a ogni tasto.
    const aggiornaApertura = (testo) => {
        setApertura(testo);
        if (timerAnteprima.current) clearTimeout(timerAnteprima.current);
        timerAnteprima.current = setTimeout(() => caricaAnteprima(integrazioniGiorni, testo), 700);
    }

    const chiediMotivo = (id, motivoCorrente) => {
        setMotivoDocId(id);
        setMotivoTesto(motivoCorrente || '');
        setMotivoVisibile(true);
    }

    const handleValidate = (id, status, note) => {
        setLoading(true);
        CompanyDocumentsService.validateCompanyDocumentWithNote(
            id,
            { isValid: status, note: note || null },
            (resp) => validateCallback(resp, id, status),
            errValidateCallback
        )
    }

    const confermaMotivo = () => {
        setMotivoVisibile(false);
        handleValidate(motivoDocId, false, motivoTesto);
    }

    const inviaRichiestaIntegrazioni = () => {
        setLoading(true);
        setIntegrazioniVisibile(false);
        CompanyDocumentsService.createIntegrationRequest(
            companyId,
            {
                note: apertura,
                responseDays: Number(integrazioniGiorni),
                isSendEmail: true,
                isSendNotification: true
            },
            (resp) => {
                setLoading(false);
                if (resp.status === 'SUCCESS') {
                    getDocsRequest(companyId);
                    setRicaricaStorico(n => n + 1);
                    if (toast.current) {
                        toast.current.show({
                            severity: 'success',
                            summary: '',
                            detail: __('Richiesta di integrazioni inviata al fornitore.', 'htr')
                        });
                    }
                } else if (toast.current) {
                    // Il backend risponde con esito negativo (es. richiesta aperta senza voci nuove): va detto.
                    toast.current.show({ severity: 'warn', summary: '', detail: resp.message || __('Richiesta non inviata.', 'htr'), life: 6000 });
                }
            },
            (resp) => {
                setLoading(false);
                if (toast.current) {
                    toast.current.show({ severity: 'error', summary: '', detail: resp.message });
                }
            }
        )
    }

    const validateCallback = useCallback((resp, id, status) => {
        if (resp.status === 'SUCCESS') {
            getDocsRequest(companyId);
        }
        setLoading(false);
    }, [rawDocsData]);

    const errValidateCallback = () => {
        setLoading(false);
    }

    const onAccettare = () => {
        setLoading(true);
        CompanyService.updateCompanyStatus(companyId, statusCallback, errStatusCallback, [
            ['companyStatus', 'APPROVED']
        ]);
    };

    const onRifiutare = () => {
        setLoading(true);
        CompanyService.updateCompanyStatus(companyId, statusCallback, errStatusCallback, [
            ['companyStatus', 'REJECTED']
        ]);
    };

    const statusCallback = (resp) => {
        if (resp.status === 'SUCCESS') {
            const company = klona(pathOr({}, ['data'], resp));
            const companies = storeGet('companies');
            const newCompanies = companies.map(o => o.id === company.id ? company : o);
            storeSet('companies', newCompanies);
            setCurrentStatus(company.companyStatus);
            if (toast.current && resp.message) {
                toast.current.show({
                    severity: 'success',
                    summary: '',
                    detail: resp.message
                });
            }
        }
        setLoading(false);
    }

    const errStatusCallback = (resp) => {
        if (toast.current && resp.message) {
            toast.current.show({
                severity: 'error',
                summary: '',
                detail: resp.message
            });
        }
        setLoading(false);
    }

    // I documenti gia' giudicati e bocciati: sono quelli che finiranno nella richiesta.
    // Attenzione: isValid nasce a false al caricamento, quindi da solo non basta a dire
    // "bocciato". Il discriminante e' il motivo, che il backend valorizza sempre quando
    // l'istruttore mette pollice giu (al minimo con il testo di default).
    const documentiBocciati = docs.filter(doc =>
        doc.isValid === false
        && doc.statusDoc !== 'NOT_AVAILABLE'
        && !isNil(doc.validationNote)
        && doc.validationNote !== ''
    );

    // Il modulo del Questionario ESG (ESG3) non si giudica col pollice: il suo stato segue la
    // verifica delle risposte (tutte conformi -> approvato) e conta come gli altri documenti.
    const docsGiudicabili = docs;
    const allDocsValid =
        docsGiudicabili.length > 0 &&
        docsGiudicabili.every(doc => doc.isValid === true) &&
        altroDocs.every(d => d.files?.length > 0 && d.files.every(f => f.isValid === true));
    const isStatusFinal = currentStatus === 'APPROVED' || currentStatus === 'REJECTED';

    // Si invia una richiesta sola, e solo dopo aver guardato ogni voce: cosi' il fornitore
    // fa un giro unico invece di riceverne una oggi e una domani.
    // "Non ancora valutato" = documento caricato che non e' ne' approvato (isValid true)
    // ne' bocciato (motivo valorizzato). isValid nasce a false al caricamento, quindi
    // isNil(isValid) non basta: userebbe un criterio che non scatta mai. Coerente con documentiBocciati.
    const nonAncoraValutati = docsGiudicabili.filter(doc =>
        doc.statusDoc !== 'NOT_AVAILABLE'
        && doc.isValid !== true
        && (isNil(doc.validationNote) || doc.validationNote === ''));
    const documentiMancanti = docs.filter(doc => doc.statusDoc === 'NOT_AVAILABLE');
    const integrazioniInviabili =
        !nonAncoraValutati.length && (documentiBocciati.length > 0 || documentiMancanti.length > 0 || risposteDaIntegrare > 0);
    const esgDaVerificare = nonAncoraValutati.some(d => d.esgType === 'ESG3');
    const altriDaValutare = nonAncoraValutati.filter(d => d.esgType !== 'ESG3');
    const motivoPulsanteSpento = nonAncoraValutati.length
        ? [
            altriDaValutare.length
                ? sprintf(__('Prima di inviare devi valutare: %s', 'htr'), altriDaValutare.map(d => d.description || d.categoryName).join(', '))
                : null,
            esgDaVerificare
                ? __('Verifica tutte le risposte del Questionario ESG ("Leggi e verifica le risposte"): l\'Autovalutazione ESG si approva da lì.', 'htr')
                : null
        ].filter(Boolean).join(' ')
        : (!documentiBocciati.length && !documentiMancanti.length && !risposteDaIntegrare
            ? __('Non ci sono voci da integrare: tutti i documenti sono a posto.', 'htr')
            : undefined);

    return (
        <div className="appPageSection__table">
            <DataTable value={docs} showGridlines stripedRows rows={5} loading={loading} dataKey="uniqueRowId"
                       filters={filters} removableSort
                       onFilter={(e) => setFilters(e.filters)}>
                <Column field="description"
                        header={__('Documento', 'htr')}
                        body={descriptionBodyTemplate}
                        filter filterField="name"
                        filterPlaceholder={__('Cerca per nome', 'htr')}
                        style={{ minWidth: '12rem' }}/>
                <Column header={__('Caricamento', 'htr')} filterField="uploadedDate" dataType="date"
                        style={{ minWidth: '8rem' }}
                        body={dateCreatedBodyTemplate} filter filterElement={dateFilterTemplate}/>
                <Column field="statusDoc" header={__('Stato Scadenza', 'htr')}
                        filterMenuStyle={{ width: '14rem' }}
                        style={{ width: '120px' }} body={statusDocBodyTemplate}
                        filterElement={statusFilterTemplate}/>
                <Column field="statusEvaluation" header={__('Stato Verifica', 'htr')}
                        filterMenuStyle={{ width: '14rem' }}
                        style={{ width: '120px' }} body={statusEvaluationBodyTemplate}
                        filterElement={statusFilterTemplate}/>
                <Column header={__('Azioni', 'htr')}
                        body={actionsBodyTemplate}/>
            </DataTable>

            <Toast ref={toast}/>

            {altroDocs.length > 0 && (
                <>
                    <div className="appPage__spacer"></div>
                    <div>
                        <div className="appPageSection__hr">
                            <span>{__('Documenti Aggiuntivi', 'htr')}</span>
                        </div>
                        {altroDocs.map(altroDoc => (
                            <div key={altroDoc.id} style={{
                                marginTop: '1rem',
                                padding: '1.5rem',
                                border: '1px solid #ddd',
                                borderRadius: '4px'
                            }}>
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: '1rem'
                                }}>
                                    <h3 style={{ margin: 0 }}>{altroDoc.description}</h3>
                                    <div className="appPageSection__iconActions">
                                        {!readOnly &&
                                            <Button icon="pi pi-bell"
                                                    disabled={altroDoc.files?.every(f => f.isValid) || !altroDoc.files?.length}
                                                    onClick={() => {
                                                        const newData = wrap({})
                                                            .set(['title'], __('Attenzione: azione richiesta'))
                                                            .set(['description'], DEFAULT_AMENDMENT_DESCRIPTION)
                                                            .value();
                                                        setAmendmentData(newData);
                                                        setIsVisibleAmendmentDialog(true);
                                                    }}
                                                    severity="info"
                                                    label={__('Sollecito', 'htr')}/>
                                        }
                                    </div>
                                </div>
                                {altroDoc.files?.length > 0 ? (
                                    <DataTable value={altroDoc.files} showGridlines dataKey="id">
                                        <Column header={__('Nome', 'htr')} style={{ width: '45%' }}
                                                body={(f) => f.fileName}/>
                                        <Column header={__('Stato Scadenza', 'htr')} style={{ width: '130px' }}
                                                body={(f) => <ProperBandoLabel status={f.status}/>}/>
                                        <Column header={__('Stato Verifica', 'htr')} style={{ width: '130px' }}
                                                body={(f) => <ProperBandoLabel
                                                    status={f.isValid ? 'APPROVED' : (f.validationNote ? 'REJECTED' : 'AWAITING')}/>}/>
                                        <Column header={__('Caricamento', 'htr')} style={{ width: '140px' }}
                                                body={(f) => {
                                                    const d = is(String, f.createdDate) ? new Date(f.createdDate) : f.createdDate;
                                                    return d instanceof Date ? d.toLocaleDateString('it-IT') : '-';
                                                }}/>
                                        <Column header={__('Azioni', 'htr')}
                                                body={(f) => (
                                                    <div className="appPageSection__iconActions">
                                                        <Button icon="pi pi-eye" rounded outlined severity="info"
                                                                disabled={!f.filePath}
                                                                onClick={() => f.filePath && apriDocumento(f.id, f.filePath, f.fileName)}
                                                                aria-label={__('Mostra', 'htr')}/>
                                                        <Button icon="pi pi-thumbs-up" rounded outlined
                                                                disabled={readOnly}
                                                                onClick={() => handleValidate(f.id, true)}
                                                                severity={!isNil(f.isValid) && f.isValid ? 'success' : 'secondary'}
                                                                aria-label={__('Si', 'htr')}/>
                                                        <Button icon="pi pi-thumbs-down" rounded outlined
                                                                disabled={readOnly}
                                                                onClick={() => handleValidate(f.id, false)}
                                                                severity={!isNil(f.isValid) && !f.isValid ? 'danger' : 'secondary'}
                                                                aria-label={__('No', 'htr')}/>
                                                    </div>
                                                )}/>
                                    </DataTable>
                                ) : (
                                    <p style={{ color: 'var(--text-color-secondary)', margin: 0 }}>
                                        {__('Nessun file caricato', 'htr')}
                                    </p>
                                )}
                            </div>
                        ))}
                    </div>
                </>
            )}

            {applicationId && (
                <>
                    <div className="appPage__spacer"></div>
                    <div>
                        <div className="appPageSection__hr">
                            <span>{__('ESG', 'htr')}</span>
                        </div>
                        <div className="appPage__spacer"></div>
                        <div style={{
                            background: '#f7f8fa', border: '1px solid #e2e5ea',
                            borderRadius: 8, padding: '14px 18px'
                        }}>
                            <p style={{ margin: '0 0 10px', fontSize: 14 }}>
                                {__('Le risposte del Questionario ESG si verificano una per una: apri la lettura e segna quelle da completare. Finiranno nella stessa richiesta di integrazioni dei documenti.', 'htr')}
                            </p>
                            <Button
                                type="button"
                                outlined
                                severity="info"
                                icon="pi pi-eye"
                                iconPos="right"
                                label={__('Leggi e verifica le risposte', 'htr')}
                                onClick={() => navigate(`/domande/${applicationId}/preview`, { state: { companyId } })}/>
                        </div>
                    </div>
                </>
            )}

            <div className="appPage__spacer"></div>

            <div className="appPageSection__hr">
                <span>{__('Azioni', 'htr')}</span>
            </div>
            <div className="appPage__spacer"></div>
            <div className="appPageSection">
                <div className="appPageSection__actions">
                    <Button
                        outlined
                        severity="warning"
                        disabled={loading || isStatusFinal || !integrazioniInviabili}
                        onClick={() => { setIntegrazioniVisibile(true); caricaAnteprima(integrazioniGiorni, apertura); }}
                        label={richiestaAperta ? __('Aggiorna richiesta di integrazioni', 'htr') : __('Richiedi integrazioni', 'htr')}
                        icon="pi pi-send"
                        iconPos="right"
                        tooltip={motivoPulsanteSpento}
                        tooltipOptions={{ position: 'top', showOnDisabled: true }}
                    />
                    <Button
                        disabled={loading || !allDocsValid || isStatusFinal}
                        onClick={onAccettare}
                        label={__('Accettare', 'htr')}
                        icon="pi pi-check"
                        iconPos="right"
                        tooltip={!allDocsValid && !isStatusFinal
                            ? (docs.some(d => d.esgType === 'ESG3' && d.isValid !== true)
                                ? __('Per accettare servono tutti i documenti approvati e tutte le risposte del Questionario ESG verificate conformi.', 'htr')
                                : __('Per accettare servono tutti i documenti approvati.', 'htr'))
                            : undefined}
                        tooltipOptions={{ position: 'top', showOnDisabled: true }}
                    />
                    <Button
                        outlined
                        disabled={loading || isStatusFinal}
                        onClick={onRifiutare}
                        label={__('Rifiutare', 'htr')}
                        icon="pi pi-times"
                        iconPos="right"
                    />
                </div>
            </div>

            <Dialog
                visible={timelineVisibile}
                modal
                style={{ maxWidth: '760px', width: '100%' }}
                header={__('Storia dei caricamenti', 'htr') + (timelineTitolo ? ' \u2014 ' + timelineTitolo : '')}
                onHide={() => setTimelineVisibile(false)}>
                {isEmpty(timelineVoci)
                    ? <p>{__('Nessuna versione da mostrare.', 'htr')}</p>
                    : <div style={{ paddingLeft: '6px' }}>
                        {timelineVoci.map((v, i) => <div key={v.id} style={{
                            position: 'relative',
                            paddingLeft: '26px',
                            paddingBottom: i === timelineVoci.length - 1 ? '4px' : '22px',
                            borderLeft: i === timelineVoci.length - 1 ? '2px solid transparent' : '2px solid #e3e8ef'
                        }}>
                            <span style={{
                                position: 'absolute', left: '-7px', top: '4px',
                                width: '12px', height: '12px', borderRadius: '50%',
                                background: v.corrente ? '#1b3a57' : '#c3ccd8',
                                border: '2px solid #fff', boxShadow: '0 0 0 2px #e3e8ef'
                            }}/>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                <strong style={{ color: '#1b3a57' }}>
                                    {v.uploadedAt ? new Date(v.uploadedAt).toLocaleString('it-IT', {
                                        day: '2-digit', month: '2-digit', year: 'numeric',
                                        hour: '2-digit', minute: '2-digit'
                                    }) : '-'}
                                </strong>
                                <ProperBandoLabel status={statoVersione(v)}/>
                                {v.corrente ? <Tag value={__('versione corrente', 'htr')} severity="info"/> : null}
                            </div>
                            <div style={{ color: '#5a6b7f', fontSize: '0.9rem', marginTop: '2px' }}>
                                {v.fileName}
                            </div>
                            <div style={{ color: '#5a6b7f', fontSize: '0.9rem' }}>
                                {__('Caricato da', 'htr')} {v.uploadedByName || '-'}
                                {v.uploadedByRoleId === 6 ? ' \u00b7 ' + __('fornitore', 'htr') : ' \u00b7 ' + __('istruttore', 'htr')}
                            </div>
                            {v.validationNote
                                ? <div style={{
                                    marginTop: '6px', padding: '8px 10px', background: '#fdf2f2',
                                    borderLeft: '3px solid #e24c4c', borderRadius: '4px',
                                    color: '#7a2b2b', fontSize: '0.9rem'
                                  }}>{v.validationNote}</div>
                                : null}
                            <div style={{ marginTop: '8px', display: 'flex', gap: '8px' }}>
                                <Button type="button" outlined size="small" icon="pi pi-eye"
                                        label={__('Apri', 'htr')}
                                        onClick={() => apriDocumento(v.id, v.fileName, v.fileName)}/>
                                <Button type="button" outlined size="small" severity="secondary" icon="pi pi-download"
                                        label={__('Scarica', 'htr')}
                                        onClick={() => scaricaDocumento(v.id)}/>
                            </div>
                        </div>)}
                    </div>}
            </Dialog>

            <Dialog
                visible={docVisibile}
                modal
                maximizable
                style={{ width: '80vw', height: '85vh' }}
                header={docNome || __('Documento', 'htr')}
                onHide={() => setDocVisibile(false)}>
                {docTipo === 'immagine'
                    ? <img src={docUrl} alt={docNome} style={{ maxWidth: '100%', maxHeight: '72vh', display: 'block', margin: '0 auto' }}/>
                    : <iframe src={docUrl} title={docNome || 'documento'} style={{ width: '100%', height: '72vh', border: 0 }}/>}
            </Dialog>

            <Dialog
                visible={motivoVisibile}
                modal
                style={{ maxWidth: '560px', width: '100%' }}
                header={__('Perche questo documento non va bene', 'htr')}
                footer={<div>
                    <Button type="button" outlined label={__('Annulla', 'htr')} onClick={() => setMotivoVisibile(false)}/>
                    <Button type="button" label={__('Conferma', 'htr')} onClick={confermaMotivo}/>
                </div>}
                onHide={() => setMotivoVisibile(false)}>
                <div className="appForm__field">
                    <p>{__('Se lasci vuoto, al fornitore arriva "Documento non conforme".', 'htr')}</p>
                    <textarea
                        rows={4}
                        style={{ width: '100%' }}
                        value={motivoTesto}
                        onChange={(e) => setMotivoTesto(e.target.value)}
                        placeholder={__('Es. il bilancio caricato e del 2023, serve l ultimo depositato.', 'htr')}/>
                </div>
            </Dialog>

            <Dialog
                visible={integrazioniVisibile}
                modal
                style={{ maxWidth: '640px', width: '100%' }}
                header={richiestaAperta ? __('Aggiorna la richiesta di integrazioni aperta', 'htr') : __('Richiedi integrazioni', 'htr')}
                footer={<div>
                    <Button type="button" outlined label={__('Annulla', 'htr')} onClick={() => setIntegrazioniVisibile(false)}/>
                    <Button type="button" label={richiestaAperta ? __('Aggiorna e invia al fornitore', 'htr') : __('Invia al fornitore', 'htr')} onClick={inviaRichiestaIntegrazioni}/>
                </div>}
                onHide={() => setIntegrazioniVisibile(false)}>
                {richiestaAperta
                    ? <div className="appPageSection__message warning" style={{ marginBottom: '.75rem' }}>
                        <i className="pi pi-info-circle"></i>
                        <span>{__('C’è già una richiesta in attesa di risposta: le voci nuove si aggiungono a quella e il fornitore riceve un nuovo avviso con l’elenco completo. Il termine viene rinnovato.', 'htr')}</span>
                    </div>
                    : null}
                <div className="appForm__field">
                    <label>{__('Giorni per rispondere', 'htr')}</label>
                    <input type="number" min="1" style={{ width: '100px' }}
                           value={integrazioniGiorni}
                           onChange={(e) => { setIntegrazioniGiorni(e.target.value); caricaAnteprima(e.target.value, apertura); }}/>
                </div>
                <div className="appForm__field">
                    <label>{__('Testo di apertura', 'htr')}</label>
                    <Editor
                        value={apertura}
                        onTextChange={(e) => aggiornaApertura(e.htmlValue || '')}
                        headerTemplate={editorHeader}
                        style={{ height: '110px' }}/>
                </div>
                <div className="appForm__field">
                    {anteprimaInCorso
                        ? <p>{__('Preparo la bozza...', 'htr')}</p>
                        : <div style={{
                              border: '1px solid #ddd', borderRadius: '6px', padding: '8px',
                              maxHeight: '360px', overflowY: 'auto', background: '#fff'
                          }} dangerouslySetInnerHTML={{ __html: anteprimaMail }}/>}
                </div>
            </Dialog>

            <Dialog
                visible={isVisibleAmendmentDialog}
                modal
                header={headerAmendmentDialog}
                footer={footerAmendmentDialog}
                style={{ maxWidth: '600px', width: '100%' }}
                onHide={hideAmendmentDialog}>
                <div className="appForm__cols">
                    <div className="appForm__field">
                        <label
                            className={classNames({ 'p-error': isEmpty(amendmentData.title) || isNil(amendmentData.title) })}>
                            {__('Titolo', 'htr')}*
                        </label>
                        <InputText value={amendmentData.title || ''}
                                   invalid={isInvalidField(amendmentData, 'title')}
                                   onChange={(e) => onUpdateFieldValue(e.target.value, 'title')}/>
                    </div>
                </div>
                <div className="appForm__cols">
                    <div className="appForm__field">
                        <label
                            className={classNames({ 'p-error': isEmpty(amendmentData.description) || isNil(amendmentData.description) })}>
                            {__('Contenuto', 'htr')}*
                        </label>
                        <Editor
                            headerTemplate={editorHeader}
                            value={amendmentData.description}
                            onTextChange={(e) => onUpdateFieldValue(e.htmlValue, 'description')}
                            style={{ height: '220px' }}
                        />
                    </div>
                </div>
            </Dialog>

            <Dialog
                visible={isVisibleMotivationDialog}
                modal
                header={headerMotivationDialog}
                footer={<Button type="button" label={__('Chiudi', 'htr')} onClick={hideMotivationDialog}/>}
                style={{ maxWidth: '600px', width: '100%' }}
                onHide={hideMotivationDialog}>
                <div>{renderHtmlContent(motivationContent)}</div>
            </Dialog>
        </div>
    )
}

export default DocumentsToEvaluateTable;
